// dependencies
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	getCurrentCompanyAdmins,
	getCurrentUserEmails,
	updateBulkEmailChanges,
	validateBulkEmailChanges,
} from "../../../../../Api/services/bulkEmailChange";

// components
import Select from "react-select";
import Button from "../../../../../Components/Button";
import { getCurrentEnvironment } from "../../../../../Selectors/app";

// styles
import css from "./BulkEmailChange.styl";

// rbac
import { getMyPermissions } from "../../../../../Api/services/me";
import { hasPermission } from "../../../../../Utils/accessControls";
import { current } from "immer";

const csvTemplate = ["Old Email", "New Email", "example1@mail.com", "example2@mail.com"];

const DisplayEmailChangeErrorsUI = (props) => {
	const { tableData } = props;

	if (!tableData) {
		return null;
	}

	return (
		<div className={css.dataTable}>
			<div>
				{tableData?.slice(1)?.some((row) => row[3]) && (
					<div className={css.errorMessage}> Please correct errors before upload. </div>
				)}
			</div>
			<table className={css.table}>
				<thead>
					<tr>
						{tableData?.[0]?.map((header, index) => (
							<th key={index}>{header}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{tableData?.slice(1)?.map((row, rowIndex) => (
						<tr key={rowIndex}>
							{row.map((cell, cellIndex) => (
								<td className={cellIndex === 3 && cell ? css.redText : ""} key={cellIndex}>
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const validateEmailChangeData = (env, tableData, companyID) => {
	const { data, status } = useQuery({
		queryKey: ["validate_bulk_email_change", "company", companyID],
		queryFn: () => validateBulkEmailChanges({ env, email_data: tableData, company_id: companyID }),
		enabled: tableData?.length > 0,
	});
	return { data, status };
};

const BulkEmailChange = (props) => {
	const dispatch = useDispatch();
	const { companyID } = props;
	const { name: env } = useSelector(getCurrentEnvironment);

	const fileInput = useRef(null);
	const [tableData, setTableData] = useState([]);
	const [processedTableData, setProcessedTableData] = useState([]);
	const [processedDataErrors, setProcessedDataErrors] = useState();
	const [userPermissions, setUserPermissions] = useState(null);
	const [bulkChangeEmailList, setBulkChangeEmailList] = useState([]);
	const [currentCompanyAdmins, setCurrentCompanyAdmins] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);

	const { data, status } = validateEmailChangeData(env, tableData, companyID);

	const { mutateAsync: bulkUpdateEmails } = useMutation({
		mutationFn: () =>
			updateBulkEmailChanges({
				env,
				email_data: processedTableData,
				company_id: companyID,
				selected_user: selectedUser?.value,
			}),
	});

	useEffect(() => {
		if (status === "success") {
			const errorsFoundData = data?.data?.errorsFoundData;
			if (errorsFoundData?.length) {
				setProcessedDataErrors(errorsFoundData);
			}
			if (!errorsFoundData?.length) {
				setProcessedTableData(tableData);
			}
		}
	}, [status, data]);

	useEffect(() => {
		const fetchPermissions = async () => {
			const result = await getMyPermissions({ env });
			setUserPermissions(result);
		};

		const fetchCurrentUserEmailData = async () => {
			const { currentUsers } = await getCurrentUserEmails({ env, companyID });

			const emailList = currentUsers?.map((user) => {
				return {
					"Display Name": user?.name,
					"Old Email": user?.email,
					"New Email": undefined,
				};
			});
			setBulkChangeEmailList(emailList);

			// Get the current company admins
			const { companyAdmins } = await getCurrentCompanyAdmins({ env, companyID });
			// Filter the users whose _id exists in the companyAdmins array
			const adminUsers = currentUsers?.filter((user) => companyAdmins.includes(user?._id));

			// Set the current company admins to the array of admin names
			setCurrentCompanyAdmins(adminUsers);
		};

		fetchCurrentUserEmailData();
		fetchPermissions();
	}, [env]);

	// reusable functions in this component
	const resetTableData = () => {
		setTableData([]);
		setProcessedTableData([]);
		setProcessedDataErrors([]);
	};

	const dispatchError = (message) => {
		dispatch({
			type: "NOTIFICATION:ERROR",
			message: message,
			actionMessage: "Dismiss",
		});
	};

	const handleDownload = (csv, filename) => {
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const hasBulkChangeEmailsPermission = hasPermission(userPermissions ?? [], "update:bulk-emails") ? "true" : "false";

	if (userPermissions?.length > 0 && hasBulkChangeEmailsPermission === "false") {
		return <div className={css.noPermissionMessage}>Sorry, you do not have permission to bulk update emails.</div>;
	}

	const handleConfirm = async () => {
		bulkUpdateEmails().then((result) => {
			const { success, usersUpdated: updatedEmailCount, errors: userUpdateErrors } = result?.data?.data;
			const emailPlural = updatedEmailCount && updatedEmailCount > 1 ? "Emails" : "Email";
			if (success && updatedEmailCount > 0) {
				dispatch({
					type: "[100] create info toast",
					message: `Updated ${updatedEmailCount} ${emailPlural}`,
					actionMessage: "Dismiss",
				});
			} else if (!success || userUpdateErrors?.length > 0) {
				if (userUpdateErrors?.length > 0) {
					dispatchError(`Error occurred updating: ${userUpdateErrors} users, please try again.`);
				} else {
					dispatchError("Error occurred, please try again.");
				}
			}
			setProcessedDataErrors();
			resetTableData();
		});
	};

	const handleUploadClick = () => {
		resetTableData();
		fileInput.current.click();
	};

	const handleDownloadCSVTemplate = () => {
		const rows = [];
		for (let i = 0; i < csvTemplate?.length; i += 2) {
			rows.push(csvTemplate?.slice(i, i + 2));
		}
		const csv = rows.map((row) => row.join(",")).join("\n");
		handleDownload(csv, "bulk_change_email_csv_template.csv");
	};

	const handleDownloadCurrentUserEmails = () => {
		if (bulkChangeEmailList?.length === 0) {
			dispatchError("Error: could not get current user email list.");
			return;
		}
		const convertToCSV = (array) => {
			const headers = Object.keys(array[0]).join(",");

			const rows = array.map((obj) => Object.values(obj).join(",")).join("\n");
			return `${headers}\n${rows}`;
		};

		const csv = convertToCSV(bulkChangeEmailList);

		handleDownload(csv, `bulk_change_email_list_company_${companyID}.csv`);
	};

	const handleFileChange = (event) => {
		setTableData([]);
		setProcessedTableData([]);
		const file = event?.target?.files?.[0];

		if (!file) {
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			const data = new Uint8Array(event.target.result);
			const csv = new TextDecoder().decode(data);
			const rows = csv.split("\n").map((row) => row?.replace(/\r/g, "")?.split(","));
			setTableData(rows);
		};
		reader.readAsArrayBuffer(file);
	};

	const uploadButtonText = processedDataErrors?.length > 0 ? "Upload New CSV" : "Upload CSV";
	const subTextMessage = !processedTableData?.length
		? "Select a bulk email change file to upload in .csv format"
		: "No errors found! Select user to attribute change to, then confirm to update emails.";

	const adminOptions = currentCompanyAdmins?.map((admin) => {
		return {
			value: admin._id,
			label: admin.name,
		};
	});

	return (
		<div>
			<div className={css.headerText}>
				For company email domain changes only. All other email changes should use the PerformYard UI.{" "}
			</div>
			<div className={css.bulkEmailChangeContainer}>
				<div className={css.buttonSpace}>
					<input type="file" ref={fileInput} style={{ display: "none" }} onChange={handleFileChange} />
					<Button onClick={handleUploadClick} className={css.button} size={"small"} color={"white"}>
						{uploadButtonText}
					</Button>
				</div>
				<div className={css.buttonSpace}>
					<Button
						onClick={handleDownloadCurrentUserEmails}
						className={css.button}
						size={"small"}
						color={"blue"}
					>
						Download Active User Emails
					</Button>
				</div>
				<div className={css.buttonSpace}>{subTextMessage}</div>
			</div>
			<div>
				<a className={`${css.downloadLink} ${css.button}`} href="#" onClick={handleDownloadCSVTemplate}>
					Download Template
				</a>
			</div>
			{processedTableData?.length > 0 ? (
				<div className={css.selectUserOptions}>
					Select user to attribute change to
					<div>
						<Select
							value={selectedUser}
							onChange={(value) => setSelectedUser(value)}
							options={adminOptions}
						></Select>
					</div>
				</div>
			) : null}
			{selectedUser?.value ? (
				<div className={css.confirmButton}>
					{processedTableData?.length > 0 ? (
						<Button
							onClick={handleConfirm}
							style={{ width: "150px" }}
							className={css.button}
							size={"large"}
							color={"green"}
						>
							Confirm Changes
						</Button>
					) : null}
				</div>
			) : null}
			{processedDataErrors?.length ? (
				<div>
					<DisplayEmailChangeErrorsUI tableData={processedDataErrors} />
				</div>
			) : null}
		</div>
	);
};

export default BulkEmailChange;
